import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';

const PurchaseDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { getPurchaseById, getSuppliers, getProducts } = useAppData();
  const [purchase, setPurchase] = useState(null);
  const suppliers = getSuppliers();
  const products = getProducts();

  useEffect(() => {
    if (location.state && location.state.purchase) {
      setPurchase(location.state.purchase);
      return;
    }
    const p = getPurchaseById(id);
    if (p) {
      setPurchase(p);
      return;
    }
    const raw = localStorage.getItem('stockpro_purchases') || localStorage.getItem('purchases');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        const found = parsed.find((x) => x.id === id || x.purchaseNumber === id);
        setPurchase(found || null);
      } catch {
        setPurchase(null);
      }
    }
  }, [id, getPurchaseById, location.state]);

  const getSupplierName = (s) => (s?.name ? s.name : '');
  const formatCurrency = (value) => `${Number(value || 0).toLocaleString()} DA`;

  const resolvedItems = useMemo(() => {
    if (!purchase || !Array.isArray(purchase.items)) return [];
    return purchase.items.map((item) => {
      const prod = products.find((p) => p.id === item.id || p.sku === item.sku);
      const price = Number(item.price ?? prod?.price ?? 0);
      const quantity = Number(item.quantity ?? 0);
      return {
        ...item,
        name: item.name || prod?.name || item.id,
        sku: item.sku || prod?.sku || item.id,
        price,
        quantity,
        total: Number(item.total ?? (price * quantity)),
      };
    });
  }, [purchase, products]);

  const totals = useMemo(() => {
    const subtotal = resolvedItems.reduce((sum, it) => sum + (Number(it.total) || 0), 0);
    return { subtotal, total: subtotal };
  }, [resolvedItems]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/purchases')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Détails de l'Achat</h1>
            <p className="text-white/60 mt-1">{id}</p>
          </div>
        </div>
      </div>

      {purchase ? (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="text-white/60 text-sm">N° Achat</div>
              <div className="text-white font-semibold">{purchase.purchaseNumber || purchase.id}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="text-white/60 text-sm">Date</div>
              <div className="text-white font-semibold">{purchase.date}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="text-white/60 text-sm">Fournisseur</div>
              <div className="text-white font-semibold">{getSupplierName(suppliers.find(s=>s.id===purchase.supplierId)) || purchase.supplierName}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="text-white/60 text-sm">Total</div>
              <div className="text-white font-semibold">{formatCurrency(totals.total)}</div>
            </div>
          </div>

          {/* Items table */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Article</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Référence</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Prix</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Qté</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {resolvedItems.map((it, idx) => (
                    <tr key={`${it.id}-${idx}`} className="hover:bg-white/5">
                      <td className="px-6 py-4 text-white">{it.name}</td>
                      <td className="px-6 py-4 text-white/80">{it.sku}</td>
                      <td className="px-6 py-4 text-right text-white/80">{formatCurrency(it.price)}</td>
                      <td className="px-6 py-4 text-right text-white/80">{it.quantity}</td>
                      <td className="px-6 py-4 text-right text-white">{formatCurrency(it.total)}</td>
                    </tr>
                  ))}
                  {resolvedItems.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-white/60">
                        Aucun article dans cet achat.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex justify-end">
              <div className="text-right">
                <div className="text-white/70 text-sm">Total: <span className="text-white font-semibold">{formatCurrency(totals.total)}</span></div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="p-6 bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 text-white/60">Achat introuvable ou non chargé.</div>
      )}
    </div>
  );
};

export default PurchaseDetail;

